import * as React from "react";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { observer } from "mobx-react-lite";
import "./OfferDialog.scss";
import offerStore from "../../../stores/OfferStore";
import { Alert, Checkbox, CircularProgress, Stack } from "@mui/material";
import { Case, ContactChoice } from "../../../stores/CaseStore/types";
import useTranslation from "../../../hooks/useTranslation";
import authStore from "../../../stores/AuthStore";
import { UserRoles } from "../../../stores/AuthStore/types";
import { Link, useNavigate } from "react-router-dom";
import { Offer } from "../../../stores/OfferStore/types";
import feedbackStore from "../../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../../stores/FeedbackStore/types";
import CaseItem from "../../Cases/CaseItem";
import configStore, { Flags } from "../../../stores/ConfigStore";

interface OfferDialogProps {
  caseItem: Case;
  open: boolean;
  setOpen: (open: boolean) => void;
  offerId?: string;
  readonly?: boolean;
}

const OfferDialog = observer<OfferDialogProps>(
  ({ caseItem, open, setOpen, offerId, readonly }) => {
    const DEFAULT_DESCRIPTION_LIMIT = parseInt(
      process.env.DEFAULT_DESCRIPTION_LIMIT || "300"
    );
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [offer, setOffer] = useState<Offer | undefined>();
    const [offerDescription, setOfferDescription] = useState(
      offer?.summary || ""
    );
    const [descriptionLimit, setDescriptionLimit] = useState(
      DEFAULT_DESCRIPTION_LIMIT
    );
    const [legalAgreement, setLegalAgreement] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({
      offerDescription: null,
    });

    useEffect(() => {
      if (authStore.userHasRole(UserRoles.PREMIUM)) {
        setDescriptionLimit(3000);
      }
    }, []);

    useEffect(() => {
      if (offerId) {
        setLoading(true);
        offerStore
          .fetchOffer(offerId)
          .then((result) => {
            setOffer(result);
            setOfferDescription(result.summary);
          })
          .finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    }, [offerId]);

    const handleClose = () => {
      setOpen(false);
    };

    const onSubmitOffer = () => {
      if (!offerDescription) {
        setErrors((prevState) => ({
          ...prevState,
          offerDescription: t("empty-offer-description-error"),
        }));
        return;
      }

      setErrors({
        offerDescription: null,
      });

      setIsSubmitting(true);

      if (offer?.id) {
        offerStore
          .updateOffer(offer.id, offerDescription)
          .then(() => {
            navigate("/home");
            setOpen(false);
          })
          .catch((error) => console.error(error))
          .finally(() => setIsSubmitting(false));
      } else {
        offerStore
          .submitOffer(offerDescription, caseItem.id, caseItem.ownerId)
          .then(() => {
            navigate("/home");
            feedbackStore.setShowFeedback(true, {
              severity: FeedbackSeverity.SUCCESS,
              message: configStore.getFlag(Flags.isOfferApprovalRequired)
                ? "your-offer-saved"
                : "your-offer-sent",
            });
          })
          .catch((error) => console.error(error))
          .finally(() => setIsSubmitting(false));
      }
    };

    return (
      <Dialog open={open} onClose={handleClose} className="offerDialog">
        {loading ? (
          <Stack
            direction={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            className={"pt pb"}
          >
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <DialogContent>
              {readonly && (
                <div className="statusFeedback">
                  {offer?.status === "PENDING" && (
                    <Alert severity={"info"}>
                      {t(offer?.reason || "no-client-action")}
                    </Alert>
                  )}
                  {offer?.status === "ON_HOLD" && (
                    <Alert severity={"warning"}>
                      {t(offer?.reason || "client-on-hold-offer")}
                    </Alert>
                  )}
                  {offer?.status === "REJECTED" && (
                    <Alert severity={"error"}>
                      {t("your-offer-rejected")}.
                      {offer?.reason ? t(offer.reason) : null}
                    </Alert>
                  )}
                  {offer?.status === "APPROVED" && (
                    <div>
                      <Alert severity={"success"}>
                        {t("client-accepted-offer")}!
                      </Alert>
                      {offer?.user && (
                        <div>
                          <h4>{t("client-information")}:</h4>
                          <p>
                            {t("first-name")}: {offer.user.firstName}
                          </p>
                          <p>
                            {t("last-name")}: {offer.user.lastName}
                          </p>
                          <h4>{t("client-preferred-contact")}:</h4>
                          {offer.user.email && <p>{offer.user.email}</p>}
                          {offer.user.phone && (
                            <p>
                              <a href={"tel:" + offer.user.phone}>
                                {offer.user.phone}&nbsp;
                                <i className="fal fa-phone" />
                              </a>
                            </p>
                          )}
                          {offer.contactChoice?.includes(
                            ContactChoice.IN_PERSON
                          ) && (
                            <p>
                              <a href={"https://wa.me/" + offer.user.phone}>
                                {offer.user.phone}&nbsp;
                                <i className="fab fa-whatsapp" />
                              </a>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <h4>{t("your-offer")}:</h4>
              <TextField
                disabled={readonly}
                error={!!errors["offerDescription"]}
                label={readonly ? "" : t("lawyer-offer-text")}
                fullWidth
                variant="outlined"
                multiline={true}
                {...(readonly ? {} : { rows: 3 })}
                value={offerDescription}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length > descriptionLimit) {
                    setErrors((prevState) => ({
                      ...prevState,
                      offerDescription: t("max-char-reached"),
                    }));
                  } else {
                    setErrors((prevState) => ({
                      ...prevState,
                      offerDescription: null,
                    }));
                  }

                  setOfferDescription(value);
                }}
              />

              {readonly ? (
                <>
                  <h4>{t("case")}:</h4>
                  <CaseItem caseItem={caseItem} />
                </>
              ) : (
                <>
                  <div className="descriptionCharCount">
                    <div>
                      {!authStore.userHasRole(UserRoles.PREMIUM) &&
                        configStore.getFlag(Flags.isPremiumEnabled) && (
                          <Link to={"/premium"}>
                            <Button
                              variant={"text"}
                              color={"warning"}
                              sx={{ textTransform: "capitalize" }}
                            >
                              {t("type-more-with-premium")}
                            </Button>
                          </Link>
                        )}
                    </div>
                    <div className="count">
                      {offerDescription.length} / {descriptionLimit}
                    </div>
                  </div>
                  {errors.offerDescription && (
                    <Alert icon={<></>} color={"error"} className="mt mb">
                      {errors.offerDescription}
                    </Alert>
                  )}

                  <h4>{t("case")}:</h4>
                  <CaseItem caseItem={caseItem} />

                  <div className="pt">
                    <h4>{t("lawyer-description-legal-warning-header")}</h4>
                    <div className="legalAgreementContainer mt">
                      <label
                        htmlFor="legalAgreement"
                        dangerouslySetInnerHTML={{
                          __html: t("lawyer-description-legal-warning"),
                        }}
                      />
                      <Checkbox
                        id="legalAgreement"
                        onChange={() =>
                          setLegalAgreement((prevState) => !prevState)
                        }
                      />
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
            {!readonly && (
              <DialogActions className="mb mr">
                <Button
                  onClick={handleClose}
                  variant={"outlined"}
                  disabled={isSubmitting}
                >
                  {t("cancel")}
                </Button>
                <Button
                  onClick={onSubmitOffer}
                  variant={"contained"}
                  disabled={
                    offerDescription.length <= 50 ||
                    !legalAgreement ||
                    !!errors.offerDescription ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <CircularProgress size={"22px"} />
                  ) : (
                    t("submit")
                  )}
                </Button>
              </DialogActions>
            )}
          </>
        )}
      </Dialog>
    );
  }
);

export default OfferDialog;
